import React, { useRef } from 'react';
import './PrintReport.css';
import { useReactToPrint } from 'react-to-print';

const PrintReport = ({ employees, matrixCategories, onClose }) => {
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: '人才盘点报告',
    onAfterPrint: onClose
  });

  // 计算各类别统计数据
  const categoryStats = matrixCategories.flat().reduce((acc, category) => {
    const count = employees.filter(emp => emp.category === category.name).length;
    acc[category.name] = {
      count,
      percentage: ((count / employees.length) * 100).toFixed(1)
    };
    return acc;
  }, {});

  // 获取技能分布
  const skillsMap = {};
  employees.forEach(emp => {
    if (emp.skills) {
      const skills = emp.skills.split(',').map(s => s.trim());
      skills.forEach(skill => {
        if (!skillsMap[skill]) {
          skillsMap[skill] = new Set();
        }
        skillsMap[skill].add(emp.name);
      });
    }
  });

  const topSkills = Object.entries(skillsMap)
    .sort(([, a], [, b]) => b.size - a.size)
    .slice(0, 10);

  return (
    <div className="print-overlay">
      <div className="print-container">
        <div className="print-controls">
          <h2>打印预览</h2>
          <div className="print-buttons">
            <button onClick={handlePrint} className="print-button">
              <span className="icon">🖨️</span>
              打印报告
            </button>
            <button onClick={onClose} className="close-button">
              <span className="icon">✕</span>
              关闭
            </button>
          </div>
        </div>
        
        <div className="print-content" ref={componentRef}>
          <div className="report-header">
            <h1>人才盘点报告</h1>
            <p className="report-date">生成日期：{new Date().toLocaleDateString()}</p>
          </div>

          <section className="report-section">
            <h2>整体概况</h2>
            <div className="summary-grid">
              <div className="summary-item">
                <div className="summary-label">总人数</div>
                <div className="summary-value">{employees.length}</div>
              </div>
              <div className="summary-item">
                <div className="summary-label">明星人才</div>
                <div className="summary-value">{categoryStats['明星人才']?.count || 0}</div>
                <div className="summary-percentage">
                  {categoryStats['明星人才']?.percentage || 0}%
                </div>
              </div>
              <div className="summary-item">
                <div className="summary-label">核心人才</div>
                <div className="summary-value">{categoryStats['核心人才']?.count || 0}</div>
                <div className="summary-percentage">
                  {categoryStats['核心人才']?.percentage || 0}%
                </div>
              </div>
            </div>
          </section>

          <section className="report-section">
            <h2>人才分布详情</h2>
            <table className="distribution-table">
              <thead>
                <tr>
                  <th>类别</th>
                  <th>人数</th>
                  <th>占比</th>
                  <th>潜力</th>
                  <th>绩效</th>
                  <th>说明</th>
                </tr>
              </thead>
              <tbody>
                {matrixCategories.flat().map(category => (
                  <tr key={category.name}>
                    <td>{category.name}</td>
                    <td>{categoryStats[category.name]?.count || 0}</td>
                    <td>{categoryStats[category.name]?.percentage || 0}%</td>
                    <td>{category.potential}</td>
                    <td>{category.performance}</td>
                    <td>{category.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="report-section">
            <h2>技能分布TOP10</h2>
            <table className="skills-table">
              <thead>
                <tr>
                  <th>技能</th>
                  <th>掌握人数</th>
                  <th>占比</th>
                  <th>掌握人员</th>
                </tr>
              </thead>
              <tbody>
                {topSkills.map(([skill, names]) => (
                  <tr key={skill}>
                    <td>{skill}</td>
                    <td>{names.size}</td>
                    <td>{((names.size / employees.length) * 100).toFixed(1)}%</td>
                    <td>{Array.from(names).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="report-section">
            <h2>IDP完成情况</h2>
            <table className="idp-table">
              <thead>
                <tr>
                  <th>类别</th>
                  <th>目标人数</th>
                  <th>已完成</th>
                  <th>进行中</th>
                  <th>未开始</th>
                </tr>
              </thead>
              <tbody>
                {matrixCategories.flat().map(category => {
                  const categoryEmployees = employees.filter(emp => emp.category === category.name);
                  const withGoals = categoryEmployees.filter(emp => emp.idpGoals?.trim());
                  const withProgress = categoryEmployees.filter(emp => emp.idpProgress?.trim());
                  
                  return (
                    <tr key={category.name}>
                      <td>{category.name}</td>
                      <td>{withGoals.length}</td>
                      <td>{withProgress.length}</td>
                      <td>{withGoals.length - withProgress.length}</td>
                      <td>{categoryEmployees.length - withGoals.length}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrintReport; 